import { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Box } from '@wix/design-system';
import ReviewsHeader from '../ReviewsHeader/ReviewsHeader';
import RenderLoadingReview from '../ReviewComponents/RenderLoadingReview';
import {
  PROFILE_REVIEWS_MAX_PER_COLUMN,
  PROFILE_REVIEWS_PER_PAGE,
  profileReviewsSectionTranslations,
} from '@utils/constants';
import { ReviewData } from '@common/types';
import { Service } from '@wix/ambassador-marketplace-categories-v1-category/types';
import { PartnerProfileEnriched } from '@common/types';
import {
  ReviewHeaderProps,
  WriteReplyToReview,
  ReviewsContentProps,
  PaginationProps,
  ReviewsMainHeaderProps,
} from '@components/Reviews/types';
import { ReviewHeader, ReviewWrapper } from '../ReviewComponents';
import s from '../ProjectsReviews.module.scss';

type ProjectsReviewsConfigProps = {
  reviews: ReviewData[];
  isLoadingNewReviews: boolean;
  projectReviewsPageCount: number;
  currentPage: number;
  serviceList: Service[];
  isMyProfile?: boolean;
  partnerProfile: PartnerProfileEnriched;
  writeReplyToReview: WriteReplyToReview;

  // Component props
  isMobileView: boolean;
  onPageChange: () => void;
  setCurrentPage: (page: number) => void;
  fetchNextPartnerProjectsPage: (page: number) => Promise<void>;
  logPaginationEvent: () => void;
};

type UseProjectsReviewsReturn = {
  contentProps: ReviewsContentProps;
  pagingProps: PaginationProps;
};

export const useProjectsReviewsConfig = ({
  reviews,
  isLoadingNewReviews,
  projectReviewsPageCount,
  currentPage,
  serviceList,
  isMyProfile,
  partnerProfile,
  writeReplyToReview,
  isMobileView,
  onPageChange,
  setCurrentPage,
  fetchNextPartnerProjectsPage,
  logPaginationEvent,
}: ProjectsReviewsConfigProps): UseProjectsReviewsReturn => {
  // ==================== COMPONENT DEFINITIONS ====================
  const ProjectReviewsHeader = useCallback(
    ({ title, reviewsCount, averageReviewRating }: ReviewsMainHeaderProps) => (
      <ReviewsHeader
        title={title}
        isMobileView={isMobileView}
        reviewsCount={reviewsCount}
        averageReviewRating={averageReviewRating}
        showReviewsCount={true}
      />
    ),
    [isMobileView],
  );

  const ProjectLoadingReviews = useCallback(
    ({
      isInitialLoading,
      isMobileView,
    }: {
      isInitialLoading: boolean;
      isMobileView?: boolean;
    }) => (
      <Box
        gap="SP3"
        className={clsx(
          s.loadingReviewsContainer,
          isMobileView && s.mobileView,
        )}
      >
        {Array.from({ length: PROFILE_REVIEWS_PER_PAGE }).map((_, i) => (
          <Box key={i} className={s.reviewsWithBorderContainer}>
            <RenderLoadingReview key={i} />
          </Box>
        ))}
      </Box>
    ),
    [],
  );

  const ProjectReviewHeader = useCallback(
    ({ review }: ReviewHeaderProps) => <ReviewHeader review={review} />,
    [],
  );

  // ==================== BUILD CONTENT CONFIGURATION ====================

  // Core data and shared functionality - changes with reviews/profile
  const coreProps = useMemo(
    () => ({
      reviews,
      reviewsCount: partnerProfile?.providerRating?.reviewsCount ?? 0,
      averageReviewRating:
        partnerProfile?.providerRating?.averageReviewRating ?? 0,
      i18nKeys: profileReviewsSectionTranslations,
      isMobileView,
      isMyProfile,
      services: serviceList,
      handleBiGenericClick: () => {},
      onEdit: undefined,
      userReview: null,
      replyProps: {
        writeReplyToReview,
        replyAuthorTitle: profileReviewsSectionTranslations.replyAuthorTitle,
        replyStyleProps: {
          writeReplyBoxVariant: 'full' as const,
          replyFormCtaButtonSkin: 'dark' as const,
          margin: isMobileView ? 'SP4 0 0 0' : 'SP5 0 0 0',
        },
      },
    }),
    [
      reviews,
      partnerProfile?.providerRating?.reviewsCount,
      partnerProfile?.providerRating?.averageReviewRating,
      isMobileView,
      isMyProfile,
      serviceList,
      writeReplyToReview,
    ],
  );

  const headerProps = useMemo(
    () => ({
      title: profileReviewsSectionTranslations.reviewsSectionTitle,
      ReviewsHeader: ProjectReviewsHeader,
      showReviewsCount: false,
      borderContainerClass: s.reviewsWithBorderContainer,
    }),
    [ProjectReviewsHeader],
  );

  // Loading functionality - changes with loading state
  const functionalityProps = useMemo(
    () => ({
      loadNewReview: isLoadingNewReviews,
      enableNoReviews: false,
      LoadingReviews: ProjectLoadingReviews,
    }),
    [isLoadingNewReviews, ProjectLoadingReviews],
  );

  // List configuration - changes when reviews change
  const listProps = useMemo(
    () => ({
      gridProps: {
        numberOfColumns:
          reviews.length > PROFILE_REVIEWS_MAX_PER_COLUMN ? 2 : 1,
        visibleAmount: PROFILE_REVIEWS_PER_PAGE,
        styles: {
          reviewsList: s.reviewsList,
          multiColumn: s.multiColumn,
          singleColumn: s.singleColumn,
          reviewColumn: s.column,
        },
      },
      reviewConfig: {
        ReviewHeader: ({ review }: { review: ReviewData }) => (
          <ProjectReviewHeader review={review} />
        ),
        reviewStyles: {
          textSize: isMobileView ? 'small' : 'medium',
        },
        ReviewComponent: ReviewWrapper,
      },
    }),
    [reviews.length, ProjectReviewHeader],
  );

  const contentProps = useMemo(
    () => ({
      ...coreProps,
      ...headerProps,
      ...functionalityProps,
      listProps,
    }),
    [coreProps, headerProps, functionalityProps, listProps],
  );

  const pagingProps = useMemo(
    () => ({
      pageSize: PROFILE_REVIEWS_PER_PAGE,
      pageNumber: currentPage,
      totalPages: projectReviewsPageCount,
      onPageChange: async (page: number) => {
        setCurrentPage(page);
        onPageChange();
        await fetchNextPartnerProjectsPage(page);
        logPaginationEvent();
      },
    }),
    [
      currentPage,
      projectReviewsPageCount,
      onPageChange,
      setCurrentPage,
      fetchNextPartnerProjectsPage,
      logPaginationEvent,
    ],
  );

  return {
    contentProps,
    pagingProps,
  };
};
